import { useQuery } from '@apollo/client';
import { NotificationEvent } from '@provision/entity-types';
import { useMemo } from 'react';
import { useGroupRules, UserGroupRules } from 'src/graphql/entities/group-rules';
import { GET_NOTIFICATION_EVENT_QUERY } from 'src/graphql/entities/notification-preference/notifications.operations';
import { NotificationEventCategory } from 'src/graphql/entities/notification-preference/notifications.types';
import { ConditionInput, QueryVariables, Records } from 'src/graphql/types';

export type NotificationEventResponse = Pick<
  NotificationEvent,
  'id' | 'category' | 'name' | 'roles'
>;

type GetNotificationEventsQuery = {
  getNotificationEvent: Records<NotificationEventResponse>;
};

const DEFAULT_FILTERS: ConditionInput[] = [
  {
    // For now, the Assignee Group Mention Feed event is not configurable by users.
    attribute: 'id',
    operator: '!=',
    value: 'assignee_group_mention_feed',
  },
];

export const useGetNotificationEvents = () => {
  const { groupRules } = useGroupRules();

  const filters = useMemo<ConditionInput[]>(() => {
    const CATEGORY_RULES: { category: NotificationEventCategory; rule: boolean }[] = [
      {
        category: 'conversation',
        rule: !!groupRules[UserGroupRules.AccessToViewConversations],
      },
    ];

    if (CATEGORY_RULES.every(({ rule }) => rule)) {
      return DEFAULT_FILTERS;
    }

    const allowedCategories = CATEGORY_RULES.filter(({ rule }) => rule).map(
      ({ category }) => category,
    );

    return [
      ...DEFAULT_FILTERS,
      {
        attribute: 'category',
        operator: 'in',
        value: allowedCategories.join(','),
      },
    ];
  }, [groupRules]);

  const { data } = useQuery<GetNotificationEventsQuery, QueryVariables>(
    GET_NOTIFICATION_EVENT_QUERY,
    {
      variables: {
        params: {
          filters,
        },
      },
    },
  );

  const [notificationEvents, notificationEventsMap] = useMemo(() => {
    const records = data?.getNotificationEvent.records || [];
    return [records, new Map(records.map((e) => [e.id, e]))];
  }, [data?.getNotificationEvent.records]);

  return {
    notificationEvents,
    notificationEventsMap,
  };
};
